"use client";

import { useEffect, useState, type ReactNode } from "react";

import { OptimizedProductImage } from "@/components/product/OptimizedProductImage";
import { cn } from "@/lib/cn";
import {
  getColorSelectionKey,
  getSafeHexColor,
  getSafeSwatchUrl,
  isSelectedColorMatch,
} from "@/lib/color-swatch-utils";
import type { Color } from "@/lib/product-utils";

interface ColorSelectionProps {
  colors: Color[];
  selectedColor: Color | null;
  showColorSwatches: boolean;
  showValidationError: boolean;
  missingColor: boolean;
  headingClassName: string;
  wrapperClassName?: string;
  optionsRowClassName?: string;
  optionsClassName?: string;
  textButtonClassName?: string;
  selectedTextButtonClassName?: string;
  unselectedTextButtonClassName?: string;
  swatchButtonClassName?: string;
  errorMessageClassName?: string;
  optionsAccessory?: ReactNode;
  onSelectColor: (color: Color) => void;
}

export function ColorSelection({
  colors,
  selectedColor,
  showColorSwatches,
  showValidationError,
  missingColor,
  headingClassName,
  wrapperClassName,
  optionsRowClassName,
  optionsClassName,
  textButtonClassName,
  selectedTextButtonClassName,
  unselectedTextButtonClassName,
  swatchButtonClassName,
  errorMessageClassName,
  optionsAccessory,
  onSelectColor,
}: ColorSelectionProps) {
  const [failedSwatchKeys, setFailedSwatchKeys] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    setFailedSwatchKeys(new Set());
  }, [colors, showColorSwatches]);

  if (colors.length === 0) return null;

  return (
    <div className={wrapperClassName}>
      <h3 className={headingClassName}>Color: {selectedColor?.label || "Not selected"}</h3>
      <div className={optionsRowClassName}>
        <div className={optionsClassName ?? "flex flex-wrap items-center gap-2"}>
          {colors.map((color: Color, index: number) => {
            const colorLabel = color.label?.trim() || `Color ${index + 1}`;
            const colorIdentity = getColorSelectionKey(color) ?? colorLabel;
            const colorOptionKey = `${colorIdentity}-${index}`;
            const selected = isSelectedColorMatch(selectedColor, color);
            const safeHexColor = getSafeHexColor(color.hex_code);
            const safeSwatchUrl = getSafeSwatchUrl(color.swatch_url);
            const swatchImageFailed = failedSwatchKeys.has(colorOptionKey);
            const showSwatchImage = typeof safeSwatchUrl === "string" && !swatchImageFailed;
            const swatchFallbackClassName =
              safeHexColor || showSwatchImage ? "bg-white" : "bg-gray-200";
            const textButtonStateClassName = selected
              ? (selectedTextButtonClassName ?? "border-gray-900 bg-gray-900 text-white")
              : (unselectedTextButtonClassName ??
                "border-gray-200 bg-white text-gray-700 hover:border-gray-300");

            if (!showColorSwatches) {
              return (
                <button
                  type="button"
                  key={colorOptionKey}
                  aria-pressed={selected}
                  className={cn(
                    "border py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2",
                    textButtonClassName ?? "px-3 transition-colors",
                    textButtonStateClassName
                  )}
                  onClick={() => onSelectColor(color)}
                >
                  {colorLabel}
                </button>
              );
            }

            return (
              <button
                type="button"
                key={colorOptionKey}
                aria-pressed={selected}
                onClick={() => onSelectColor(color)}
                className={cn(
                  "relative h-8 w-8 rounded-full border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2",
                  swatchFallbackClassName,
                  selected
                    ? "scale-105 border-gray-900 ring-2 ring-gray-900 ring-offset-2"
                    : "border-gray-300 hover:border-gray-500",
                  swatchButtonClassName
                )}
                style={safeHexColor ? { backgroundColor: safeHexColor } : undefined}
                aria-label={`Select color ${colorLabel}`}
                title={colorLabel}
              >
                {showSwatchImage ? (
                  <OptimizedProductImage
                    src={safeSwatchUrl}
                    alt=""
                    fill
                    className="rounded-full object-cover"
                    sizes="32px"
                    unoptimized={true}
                    onError={() => {
                      setFailedSwatchKeys((previousKeys) => {
                        if (previousKeys.has(colorOptionKey)) return previousKeys;

                        const nextKeys = new Set(previousKeys);
                        nextKeys.add(colorOptionKey);
                        return nextKeys;
                      });
                    }}
                  />
                ) : null}
              </button>
            );
          })}
        </div>
        {optionsAccessory}
      </div>
      {showValidationError && missingColor && (
        <p className={errorMessageClassName ?? "mt-2 text-xs text-red-500"}>
          Please select a color
        </p>
      )}
    </div>
  );
}
